import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  BookOpen, ShieldCheck, ChartLineUp, ArrowRight,
  Bank, CurrencyCircleDollar, Receipt, FileText,
  ChartBar, Scales, Handshake, Globe,
  Calculator, Briefcase, Users, TrendUp, Gavel
} from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { ServiceSchema, BreadcrumbSchema } from '../components/StructuredData';

const bookkeepingFeatures = [
  { icon: Bank, title: 'Daily Data Ingestion', desc: 'Ensure precise, real-time financial records with consistent and reliable data synchronization.' },
  { icon: Receipt, title: 'Bank-Reconciled Books (T+1)', desc: 'Every cleared bank and gateway transaction is matched and posted within 24 hours, giving you a clean, audit-ready ledger at all times.' },
  { icon: CurrencyCircleDollar, title: 'Cash & Burn Tracking', desc: 'Real-time visibility into cash runway and burn rate.' },
  { icon: FileText, title: 'AR/AP Aging', desc: 'Automated receivables/payables tracking with follow-up workflows.' },
  { icon: ChartBar, title: 'Monthly Close (T+3-5)', desc: 'Structured SOP-driven monthly close with variance analysis.' },
  { icon: Calculator, title: 'Payment Gateway Recon', desc: 'Settlement reconciliation for Razorpay, Stripe, PayPal, and more.' },
];

const complianceFeatures = [
  { icon: Scales, title: 'India: GST/TDS/Companies Act', desc: 'GST returns, TDS filings, PF/ESI/Professional Tax returns across India and Companies Act compliance.' },
  { icon: Globe, title: 'US: Tax & Compliance', desc: 'Complete US tax support covering federal and state income tax returns (1040/1120/1165), sales tax nexus monitoring, 1099 preparation, multi-state apportionment and quarterly estimated tax filings — fully compliant with IRS deadlines.' },
  { icon: ShieldCheck, title: 'UAE: Tax & Regulatory Compliance', desc: 'End-to-end compliance covering VAT returns, Corporate Tax filing, Transfer Pricing disclosures, ESR reporting, and UBO registry maintenance. We manage all FTA registrations, amendments, and deadlines.' },
];

const dtaaFeatures = [
  { icon: Gavel, title: 'Withholding Tax Applicability', desc: 'Assessment of withholding tax obligations on cross-border payments and transactions.' },
  { icon: Globe, title: 'DTAA Benefit Evaluation', desc: 'Evaluation of Double Taxation Avoidance Agreement benefits to optimise tax efficiency across jurisdictions.' },
  { icon: FileText, title: 'Form 15CA/15CB Preparation', desc: 'Preparation of Form 15CA/15CB for foreign remittances — fully compliant with FEMA and Income Tax Act requirements.' },
];

const cfoFeatures = [
  { icon: TrendUp, title: 'Budgeting & Forecasting', desc: 'Rolling monthly and quarterly financial forecasts with scenario modelling for best, base, and stress cases. Helps you plan hiring, capex, and cash flow with confidence — not guesswork.' },
  { icon: ChartLineUp, title: 'Unit Economics & Margin Analysis', desc: 'Deep-dive into LTV/CAC ratios, contribution margins, and per-product or per-customer profitability. Know exactly which revenue streams are building value and which are draining it.' },
  { icon: Briefcase, title: 'Board & Investor Reporting', desc: 'Investor-grade board packs with financial KPIs, actuals vs. budget variance commentary, and business narrative. Delivered on time, every month — board-ready without last-minute scrambles.' },
  { icon: Handshake, title: 'Fundraising & Data Room Support', desc: 'Financial models, pitch deck financial slides, investor MIS, and data room preparation for seed to Series B rounds. We speak the language investors expect.' },
  { icon: Users, title: 'KPI Dashboard & Performance Tracking', desc: 'Custom KPI frameworks tracking MRR, churn, burn multiple, CAC payback, gross margin, and AOV. Real-time visibility into the metrics that drive your next funding round or growth decision.' },
  { icon: Globe, title: 'Cross-Border Structuring & Treasury', desc: 'Entity structuring advice for India-UAE-US operations, cross-border compliance, intercompany pricing, and banking setup. One CFO partner across all your geographies — no coordination gaps.' },
];

function ServiceBlock({ id, icon: Icon, title, overline, description, features, reversed, gridCols = 3 }) {
  return (
    <section id={id} data-testid={`service-${id}`} className={`border-b border-gray-200 ${reversed ? 'bg-[#F9FAFB]' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-6 py-20">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">{overline}</p>
        <div className="flex items-start gap-4 mb-4">
          <Icon size={36} weight="duotone" className="text-[#002FA7] flex-shrink-0 mt-1" />
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black">{title}</h2>
        </div>
        <p className="text-base text-gray-600 max-w-2xl mb-12">{description}</p>
        <div className={`grid grid-cols-1 md:grid-cols-${gridCols} gap-0 border border-black/10`}>
          {features.map((f, i) => (
            <div key={i} className={`p-6 md:p-8 ${i < features.length - 1 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''} ${i >= gridCols ? 'md:border-t border-black/10' : ''}`}>
              <f.icon size={24} weight="duotone" className="text-[#002FA7] mb-3" />
              <h4 className="text-sm font-bold text-black mb-2">{f.title}</h4>
              <p className="text-xs text-gray-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <div>
      <SEO
        title="Services - Bookkeeping, Compliance & CFO"
        description="Daily bookkeeping (T+1), multi-jurisdiction compliance (India, US, UAE), international taxation & DTAA, and fractional CFO services. End-to-end finance operations."
        path="/services"
      />
      <ServiceSchema name="Daily AI-Bookkeeping" description="Automated daily bookkeeping with T+1 posting, bank reconciliation, and ML-powered categorization." />
      <ServiceSchema name="Multi-Jurisdiction Compliance" description="GST, TDS, VAT, Corporate Tax compliance across India, US, and UAE." />
      <ServiceSchema name="Fractional CFO" description="FP&A, budgeting, forecasting, board packs, fundraising support, and cross-border advisory." />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Services', path: '/services' }]} />

      {/* Hero */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-6">Our Services</p>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none text-black mb-6">
            Finance operations,<br />handled <span className="text-[#002FA7]">daily</span>.
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mb-8">
            From daily data ingestion to board-ready insights. We handle your books, compliance, and financial strategy so you can focus on building.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#bookkeeping"><Button className="bg-black text-white hover:bg-gray-800 rounded-[4px] text-sm px-6">Bookkeeping</Button></a>
            <a href="#compliance"><Button variant="outline" className="border-black/20 rounded-[4px] text-sm px-6">Compliance</Button></a>
            <a href="#dtaa"><Button variant="outline" className="border-black/20 rounded-[4px] text-sm px-6">International Tax</Button></a>
            <a href="#cfo"><Button variant="outline" className="border-black/20 rounded-[4px] text-sm px-6">FP&A / CFO</Button></a>
          </div>
        </div>
      </section>

      <ServiceBlock
        id="bookkeeping"
        icon={BookOpen}
        title="Daily Bookkeeping"
        overline="Core Service"
        description="Automated data ingestion, ML-powered categorization, and expert human review. Your books reconciled daily, not monthly."
        features={bookkeepingFeatures}
      />

      <ServiceBlock
        id="compliance"
        icon={ShieldCheck}
        title="Multi-Jurisdiction Compliance"
        overline="India / US / UAE"
        description="GST, TDS/Withholding Tax, VAT, Corp Tax across India, US and UAE. We handle the filings so you stay compliant everywhere."
        features={complianceFeatures}
        gridCols={3}
        reversed
      />

      <ServiceBlock
        id="dtaa"
        icon={Gavel}
        title="International Taxation & DTAA Compliance"
        overline="Cross-Border"
        description="Advisory and compliance for cross-border transactions including withholding tax applicability, DTAA benefit evaluation and Form 15CA/15CB preparation for foreign remittances — fully compliant with FEMA and Income Tax Act requirements."
        features={dtaaFeatures}
        gridCols={3}
      />

      <ServiceBlock
        id="cfo"
        icon={ChartLineUp}
        title="FP&A / Fractional CFO"
        overline="Strategic Finance"
        description="From unit economics to fundraising support. Get CFO-grade insights without the full-time hire."
        features={cfoFeatures}
        reversed
      />

      {/* Add-ons */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">Extras</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-8">Add-ons</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-black/10 bg-white">
            {[
              { title: 'CFO Hours', desc: 'On-demand fractional CFO time for strategic decisions.' },
              { title: 'Audit Prep', desc: 'Per engagement audit preparation and support.' },
              { title: 'Payroll', desc: 'Full payroll processing and compliance.' },
              { title: 'Tax (US/UAE)', desc: 'Inhouse CPAs for US and UAE tax compliance.' },
            ].map((a, i) => (
              <div key={i} className={`p-6 md:p-8 ${i < 3 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''}`}>
                <h4 className="text-sm font-bold text-black mb-2">{a.title}</h4>
                <p className="text-xs text-gray-600">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Ready to get started?</h2>
          <p className="text-gray-400 mb-8 text-sm">Clean-up + first month daily close for a fixed fee. Keep what we set up even if you leave.</p>
          <Link to="/#lead-form">
            <Button data-testid="services-cta" className="bg-[#002FA7] hover:bg-[#002277] text-white rounded-[4px] px-8 py-3 text-sm font-semibold">
              Get Free Finance Health Check <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
